import React, { Fragment, PureComponent } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {
    UnfoldMoreRounded,
    UnfoldLessRounded,
    ArrowDropDownRounded,
    ArrowDropUpRounded
} from "@mui/icons-material";

import { CurrencyTD, Td, DescriptionTD, NoPrintDiv, SmallOnly, LargeOnly } from "commons";
import { format } from "commons/src/NuDateUtils";

import { FeesTD } from "./FeesTD";
import { BED_ISA_CODE } from "./SignificantTypeCodes";
import { ISA } from "./ProductShortCodes";
import { TrxTr } from "./styled/TrxTr";
import { IHTr } from "./styled/IHTr";
import { isItalic } from "./helpers/transactionEventHelpers";

export class TransactionItem extends PureComponent {
    static getTrevDescription = trev => {
        const descriptionComponents = [trev.trevTtypDescription];

        if (
            trev.trevMappings.EVENT_REASON_CODE &&
            JSON.parse(trev.trevMappings.EVENT_REASON_CODE)
        ) {
            descriptionComponents.push(trev.trevReason);
        }
        if (trev.trevMappings.EVENT_REF && JSON.parse(trev.trevMappings.EVENT_REF)) {
            descriptionComponents.push(trev.trevReference);
        }
        if (
            trev.trevMappings.EVENT_TRANSFER_ACCOUNT &&
            JSON.parse(trev.trevMappings.EVENT_TRANSFER_ACCOUNT)
        ) {
            descriptionComponents.push(trev.trevTransferAccount);
        }
        descriptionComponents.push(trev.trevCommitmentPlanNumber);

        const displayStatus = TransactionItem.isEventUnsettledTransfer(trev)
            ? ` (${trev.eventStatus})`
            : "";

        return (
            descriptionComponents.filter(e => e && e.length > 0).join(" - ") + `${displayStatus}`
        );
    };

    static isEventUnsettledTransfer(trev) {
        return trev.eventStatus !== "Completed" && trev.eventStatus !== "None";
    }

    showHideInvestmentTransactions = () => {
        const val = !this.props.isShowInvestments;
        this.props.hideShowInvestments(this.props.transactionEvent.trevId, val);
    };

    getTrevAmount = () => {
        return this.props.transactionEvent.trevAmount === 0
            ? null
            : this.props.transactionEvent.trevAmount;
    };

    isBedAndIsaApplication = () => {
        return (
            this.props.accountProductType === ISA &&
            this.props.transactionEvent.trevTtypCode === BED_ISA_CODE
        );
    };

    runningCashDisplay = () => {
        if (this.props.runningCash[this.props.transactionEvent.trevId] === undefined) {
            return 0;
        }

        if (this.isBedAndIsaApplication()) {
            return null;
        }

        return this.props.runningCash[this.props.transactionEvent.trevId];
    };

    renderIncomeHistoryRow = () => {
        const linkShown = this.props.hasChildren && !this.props.searchApplied;
        return (
            <IHTr
                className="transaction-event"
                isClientPortal={this.props.isClientPortal}
                italic={isItalic(
                    this.props.transactionEvent.pending,
                    TransactionItem.isEventUnsettledTransfer(this.props.transactionEvent)
                )}
                onClick={
                    this.props.isClientPortal && linkShown
                        ? this.showHideInvestmentTransactions
                        : () => {}
                }
            >
                <Td>{format(this.props.transactionEvent.trevEffectiveDate)}</Td>

                <DescriptionTD>
                    {this.props.narrative}
                    {linkShown && (
                        <LargeOnly enabled={this.props.isClientPortal}>
                            <NoPrintA onClick={this.showHideInvestmentTransactions}>
                                <Icon isShowInvestments={this.props.isShowInvestments} />
                            </NoPrintA>
                        </LargeOnly>
                    )}
                </DescriptionTD>
                {this.props.isClientPortal && (
                    <SmallOnly>
                        <CurrencyTD amount={this.getTrevAmount()} />
                    </SmallOnly>
                )}
                <LargeOnly enabled={this.props.isClientPortal}>
                    <Fragment>
                        <CurrencyTD amount={this.getTrevAmount()} />
                    </Fragment>
                </LargeOnly>
                {this.props.isClientPortal && (
                    <SmallOnly>
                        <td>
                            {linkShown && (
                                <ClientPortalExpandLink>
                                    <Icon
                                        isShowInvestments={this.props.isShowInvestments}
                                        isClientPortal={true}
                                    />
                                </ClientPortalExpandLink>
                            )}
                        </td>
                    </SmallOnly>
                )}
            </IHTr>
        );
    };

    renderTransactionRow = () => {
        const isEventUnsettledTransfer = TransactionItem.isEventUnsettledTransfer(
            this.props.transactionEvent
        );
        const putAmountInFeesColumn =
            this.props.transactionEvent.accountTransactions[0] &&
            this.props.transactionEvent.accountTransactions[0].investmentTransactions[0] &&
            this.props.transactionEvent.accountTransactions[0].investmentTransactions[0]
                .itrnMappings.TRX_HIGH_LEVEL === "Fees and charges";

        const linkShown = this.props.hasChildren && !this.props.searchApplied;
        return (
            <TrxTr
                data-testid="transaction-event"
                className="transaction-event"
                isClientPortal={this.props.isClientPortal}
                italic={isItalic(this.props.transactionEvent.pending, isEventUnsettledTransfer)}
                onClick={
                    this.props.isClientPortal && linkShown
                        ? this.showHideInvestmentTransactions
                        : () => {}
                }
            >
                <Td>{format(this.props.transactionEvent.trevEffectiveDate)}</Td>
                {this.props.isClientPortal && (
                    <SmallOnly>
                        <CurrencyTD
                            data-testid="small-cp-running-cash-cell"
                            amount={this.runningCashDisplay()}
                        />
                    </SmallOnly>
                )}
                <Td>{format(this.props.transactionEvent.trevProcessedDate)}</Td>
                <LargeOnly enabled={this.props.isClientPortal}>
                    <Td data-testid="large-reporting-date-cell">
                        {format(this.props.transactionEvent.trevReportingDate)}
                    </Td>
                </LargeOnly>
                <DescriptionTD>
                    {this.props.narrative}
                    {linkShown && (
                        <LargeOnly enabled={this.props.isClientPortal}>
                            <NoPrintA onClick={this.showHideInvestmentTransactions}>
                                <Icon isShowInvestments={this.props.isShowInvestments} />
                            </NoPrintA>
                        </LargeOnly>
                    )}
                </DescriptionTD>
                {this.props.isClientPortal && (
                    <SmallOnly>
                        <CurrencyTD
                            data-testid="small-cp-amount-cell"
                            amount={putAmountInFeesColumn ? null : this.getTrevAmount()}
                        />
                    </SmallOnly>
                )}
                <Td />
                <Td />
                <FeesTD
                    fees={putAmountInFeesColumn ? [this.getTrevAmount()] : this.props.filteredFees}
                    amountInFeesColumn={putAmountInFeesColumn}
                    expandedState={
                        (this.props.isShowInvestments || this.props.searchApplied) &&
                        this.props.hasChildren
                    }
                />

                <LargeOnly enabled={this.props.isClientPortal}>
                    <Fragment>
                        <CurrencyTD
                            data-testid="large-amount-cell"
                            amount={putAmountInFeesColumn ? null : this.getTrevAmount()}
                        />
                        <CurrencyTD
                            data-testid="large-running-cash-cell"
                            amount={this.runningCashDisplay()}
                        />
                    </Fragment>
                </LargeOnly>
                {this.props.isClientPortal && (
                    <SmallOnly>
                        <td>
                            {linkShown && (
                                <ClientPortalExpandLink>
                                    <Icon
                                        isShowInvestments={this.props.isShowInvestments}
                                        isClientPortal={true}
                                    />
                                </ClientPortalExpandLink>
                            )}
                        </td>
                    </SmallOnly>
                )}
            </TrxTr>
        );
    };

    render() {
        return this.props.isIncomeHistory
            ? this.renderIncomeHistoryRow()
            : this.renderTransactionRow();
    }
}

export const Icon = ({ isShowInvestments, isClientPortal }) => {
    const iconProps = {
        color: "primary",
        style: { fontSize: "1rem", cursor: "pointer", marginLeft: "4px" }
    };

    const cpIconProps = {
        color: "primary",
        fontSize: "large"
    };

    if (isClientPortal) {
        return isShowInvestments ? (
            <ArrowDropUpRounded {...cpIconProps} aria-label="show less" />
        ) : (
            <ArrowDropDownRounded {...cpIconProps} aria-label="show more" />
        );
    }

    return isShowInvestments ? (
        <UnfoldLessRounded {...iconProps} aria-label="show less" />
    ) : (
        <UnfoldMoreRounded {...iconProps} aria-label="show more" />
    );
};

Icon.propTypes = {
    isShowInvestments: PropTypes.bool,
    isClientPortal: PropTypes.bool
};

export const NoPrintA = styled(NoPrintDiv.withComponent("a"))`
    display: flex;
    align-items: center;
`;

export const ClientPortalExpandLink = styled.div`
    position: relative;
    font-size: 25px;
    top: 38px;
    color: #000;
    padding-left: 50%;
    margin-left: -15px;
    width: 0;
`;

TransactionItem.propTypes = {
    hideShowInvestments: PropTypes.func,
    isShowInvestments: PropTypes.bool,
    hasChildren: PropTypes.bool,
    runningCash: PropTypes.object,
    transactionEvent: PropTypes.shape({
        accountTransactions: PropTypes.array,
        trevTtypCode: PropTypes.string,
        trevEffectiveDate: PropTypes.string,
        trevProcessedDate: PropTypes.string,
        trevReportingDate: PropTypes.string,
        trevTtypDescription: PropTypes.string,
        eventStatus: PropTypes.string,
        trevReference: PropTypes.string,
        trevAmount: PropTypes.number,
        trevReason: PropTypes.string,
        trevTransferAccount: PropTypes.string,
        trevId: PropTypes.number,
        trevFees: PropTypes.array,
        trevCommitmentPlanNumber: PropTypes.string,
        trevMappings: PropTypes.object,
        pending: PropTypes.bool
    }),
    accountProductType: PropTypes.string,
    filteredFees: PropTypes.array,
    searchApplied: PropTypes.bool,
    narrative: PropTypes.string,
    isClientPortal: PropTypes.bool,
    isIncomeHistory: PropTypes.bool
};

TransactionItem.defaultProps = {
    isIncomeHistory: false
};
